import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { Badge, Button, Card, EmptyState, ErrorText, Field, Select, Spinner } from '../../components/ui'
import { Icon } from '../../components/ui/icons'
import { downloadDocument } from '../../services/documents'
import { DocumentViewer, type ViewableDocument } from '../../components/documents/DocumentViewer'
import {
  fileSize,
  formatDate,
  PATIENT_DOCUMENT_TYPE_COLORS,
  PATIENT_DOCUMENT_TYPE_LABELS,
} from '../../utils/format'

interface PortalDocument extends ViewableDocument {
  type: string
  createdAt: string
}

/**
 * Mis exámenes: resultados de laboratorio e imágenes que la clínica publicó
 * para este paciente. El archivo se pide autenticado y se abre en memoria —
 * no hay una URL del examen que se pueda compartir sin querer.
 */
export function MisExamenesPage() {
  const [type, setType] = useState('')
  const [viewing, setViewing] = useState<PortalDocument | null>(null)
  const [error, setError] = useState('')

  const documents = useQuery({
    queryKey: ['patient-documents-portal'],
    queryFn: async () => (await api.get<PortalDocument[]>('/patient-portal/documents')).data,
  })

  const list = (documents.data ?? []).filter((d) => !type || d.type === type)

  return (
    <>
      <Card
        title="Mis exámenes"
        actions={
          <div className="w-44">
            <Field label="Filtrar por tipo">
              <Select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="">Todos</option>
                <option value="LABORATORY">Laboratorio</option>
                <option value="IMAGING">Imagenología</option>
                <option value="REPORT">Informes</option>
                <option value="OTHER">Otros</option>
              </Select>
            </Field>
          </div>
        }
      >
        <ErrorText>{error}</ErrorText>
        {documents.isLoading ? (
          <Spinner />
        ) : list.length === 0 ? (
          <EmptyState message="Todavía no hay exámenes publicados. Cuando la clínica cargue tus resultados los verás aquí." />
        ) : (
          <ul className="space-y-2">
            {list.map((doc) => (
              <li key={doc.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 p-3">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 font-medium text-ink">
                    <Icon name={doc.mimeType.startsWith('image/') ? 'imaging' : 'document'} className="h-4 w-4 text-steel-500" />
                    {doc.title}
                    <Badge className={PATIENT_DOCUMENT_TYPE_COLORS[doc.type]}>{PATIENT_DOCUMENT_TYPE_LABELS[doc.type]}</Badge>
                  </p>
                  <p className="text-xs text-gray-500">
                    {doc.examDate ? `Examen del ${formatDate(doc.examDate)}` : `Cargado el ${formatDate(doc.createdAt)}`} ·{' '}
                    {fileSize(doc.sizeBytes)}
                  </p>
                  {doc.notes && <p className="mt-1 text-sm text-steel-700">{doc.notes}</p>}
                </div>
                {/* Acciones con texto, no íconos sueltos: el ícono de descarga
                    solo no se entendía (pedido de Chejo, minuta 15-09). */}
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button variant="secondary" onClick={() => setViewing(doc)}>
                    <Icon name="view" className="mr-1.5 inline h-4 w-4" />
                    Ver informe
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => downloadDocument('patient', doc.id, doc.fileName).catch((err) => setError(apiErrorMessage(err)))}
                  >
                    <Icon name="download" className="mr-1.5 inline h-4 w-4" />
                    Descargar informe
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-xs text-gray-400">
          Tus resultados son datos sensibles: cada vez que los abres o descargas queda registrado. No los compartas por
          canales abiertos.
        </p>
      </Card>

      {viewing && <DocumentViewer document={viewing} source="patient" onClose={() => setViewing(null)} />}
    </>
  )
}
